import { useState, type ReactNode } from 'react';

/**
 * Een miniatuur die terugvalt op de placeholder zodra het bestand niet laadt.
 *
 * Waarom dit bestaat: de kaarten kozen tussen afbeelding en placeholder op de
 * vraag of er een URL BEKEND is, niet of die URL nog iets oplevert. Een
 * verwijderde of verkeerd gecodeerde upload gaf daardoor het kapotte-plaatje-
 * icoontje van de browser — een leeg vlak met een gescheurd hoekje linksboven,
 * waar de placeholder al klaarstond. Hij werd alleen te vroeg weggekozen.
 *
 * `brokenSrc` onthoudt WELKE bron faalde in plaats van een kale boolean. De
 * kaarten worden hergebruikt bij filteren, sorteren en pagineren; een boolean
 * zou blijven plakken op het volgende artikel dat die plek in de lijst krijgt,
 * en dan verdwijnt een afbeelding die het prima doet.
 *
 * De ref-callback dekt het geval dat het laden al mislukt is vóórdat React
 * hydrateert: het error-event is dan al langsgekomen en `onError` vuurt nooit
 * meer. `complete` met een `naturalWidth` van 0 betekent precies dat — geladen,
 * maar er kwam niets bruikbaars terug.
 *
 * `srcSet`/`sizes` zijn er voor de verkleinde varianten (shared/image-url.ts):
 * de kaart vraagt dan een WebP van 480 of 800 px in plaats van het origineel.
 */
export function ThumbImage({
  src,
  srcSet,
  sizes,
  className,
  fallback,
}: {
  src: string;
  srcSet?: string;
  sizes?: string;
  className?: string;
  /** Wat er komt te staan als het bestand niet laadt — dezelfde placeholder
   *  als wanneer het artikel helemaal geen afbeelding heeft. */
  fallback: ReactNode;
}) {
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);
  if (brokenSrc === src) return <>{fallback}</>;

  return (
    <img
      src={src}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      alt=""
      loading="lazy"
      decoding="async"
      className={className}
      ref={(el) => {
        if (el && el.complete && el.naturalWidth === 0) setBrokenSrc(src);
      }}
      onError={() => setBrokenSrc(src)}
    />
  );
}
